import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ACADEMY_LOGO, IMAGES } from "../data/assets";
import { usePlayerAuth } from "../context/PlayerAuthContext";
import { ShareConsentCheckbox } from "../components/ShareConsentCheckbox";
import { Ticket, Crown, Loader2, CheckCircle2, ArrowRight, Zap } from "lucide-react";

const PERKS = [
  "Full Elite Hub — premium HUD, performance rings & gold theme",
  "Unlimited coach video reviews with personal feedback",
  "Monthly Elite Roadmap + priority leaderboard ranking",
  "Your points, progress & videos are saved if you upgrade later",
];

export default function Redeem() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { redeem, player } = usePlayerAuth();
  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [jersey, setJersey] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    const c = params.get("code");
    if (c) setCode(c.toUpperCase());
    if (player?.email) setEmail(player.email);
  }, [params, player]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const p = await redeem({
        code: code.trim().toUpperCase(),
        email: email.trim(),
        password,
        name: name.trim(),
        display_name: displayName.trim(),
        jersey_number: jersey.trim(),
        share_consent: consent,
      });
      toast.success(`You're in! 7-day Elite trial unlocked for ${p.display_name || p.name || "your player"}.`);
      navigate("/hub");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't redeem that code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-sm text-white placeholder:text-white/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]";

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden relative">
      <div className="absolute inset-0">
        <img loading="lazy" decoding="async" src={IMAGES.hero} alt="" className="w-full h-full object-cover object-center opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-[#0a0a0a]/92 to-[#0a0a0a]" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 py-10 sm:py-16">
        <button data-testid="redeem-logo-home" onClick={() => navigate("/")} className="flex items-center gap-3 mb-10">
          <img src={ACADEMY_LOGO} alt="The Next O Technical Academy" className="w-36 h-auto object-contain" />
          <div className="text-left">
            <div className="font-headings text-base uppercase tracking-wide leading-none">Next O Soccer</div>
            <div className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mt-1">Elite Team Trial</div>
          </div>
        </button>

        <div className="grid md:grid-cols-2 gap-10 items-start">
          {/* Left: pitch */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37] bg-[#D4AF37]/12 border border-[#D4AF37]/40 rounded-full px-3 py-1.5 mb-5">
              <Crown size={13} /> 7-Day Free Elite Trial
            </span>
            <h1 className="font-cover text-5xl sm:text-6xl uppercase leading-[0.9] tracking-tight mb-5">
              Redeem Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F5E7B8] via-[#D4AF37] to-[#B8912E]">Team Code</span>
            </h1>
            <p className="text-white/65 text-base mb-8 max-w-md">
              Got a code from your coach? Unlock <b className="text-white">7 days of full Elite access</b> and join your team's private leaderboard — no card required.
            </p>
            <ul className="space-y-3">
              {PERKS.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-white/80">
                  <CheckCircle2 size={18} className="text-[#D4AF37] shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Right: form */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="glass-gold rounded-2xl border border-[#D4AF37]/30 bg-[#111]/80 backdrop-blur-xl p-6 sm:p-7" data-testid="redeem-page">
            <div className="flex items-center gap-2 mb-1"><Zap size={16} className="text-[#D4AF37]" /><h2 className="font-headings text-2xl uppercase tracking-wide">Start My Free Trial</h2></div>
            <p className="text-white/50 text-sm mb-5">Takes under a minute.</p>
            <form onSubmit={submit} className="space-y-3">
              <div className="relative">
                <Ticket size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
                <input data-testid="redeem-code" required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Team code (e.g. U9BOYS-7K4Q)"
                  className={field.replace("p-3", "py-3 pl-9 pr-3") + " font-mono uppercase tracking-wider"} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="redeem-display-name" required value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Player name" className={field} />
                <input data-testid="redeem-jersey" value={jersey} onChange={(e) => setJersey(e.target.value)} placeholder="Jersey # (optional)" className={field} />
              </div>
              <input data-testid="redeem-parent-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Parent / account name (optional)" className={field} />
              <input data-testid="redeem-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={field} />
              <input data-testid="redeem-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Create a password (min 6 chars)" className={field} />
              <ShareConsentCheckbox testid="redeem-share-consent" checked={consent} onChange={setConsent} />
              <button data-testid="redeem-submit" type="submit" disabled={busy}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#E7C64B] to-[#C39A2B] hover:brightness-110 text-black font-subheadings font-bold uppercase tracking-wider py-3.5 rounded-sm transition-all disabled:opacity-60">
                {busy ? <><Loader2 size={16} className="animate-spin" /> Redeeming…</> : <>Start My Free Trial <ArrowRight size={16} /></>}
              </button>
            </form>
            <p className="text-xs text-white/45 mt-3">
              Already have an account with this email? Enter your existing password above to add the trial, or{" "}
              <button data-testid="redeem-to-login" onClick={() => navigate("/")} className="text-[#D4AF37] hover:underline">log in here</button>.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
